/**
 * Pure derivations for the agency overview, customer list, delivery calendar and WhatsApp inbox screens.
 * Everything here is computed from fields the partner API already returns; where the API has no field the screen says so
 * (see SAMPLE_CONVERSATIONS, which is labelled "Sample data" wherever it is drawn).
 */
import type { DeliverableState } from '@qm/contracts/deliverables';
import type { ClientView, WaNumberView, WeekItem } from '../api';

// ------------------------------------------------------------------ customers
/** Masks the middle of a phone number for display: "+971500000042" -> "+971 ••••• 42". Never returns the full number. */
export function maskPhone(phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (digits.length < 7) return '';
  return `+${digits.slice(0, 3)} ${'•'.repeat(Math.min(digits.length - 5, 5))} ${digits.slice(-2)}`;
}
/** Display phone per client tenant from the agency's WhatsApp numbers (first number wins). */
export function phoneByClient(numbers: readonly Pick<WaNumberView, 'clientTenantId' | 'displayPhone'>[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const n of numbers) if (!(n.clientTenantId in out)) out[n.clientTenantId] = n.displayPhone;
  return out;
}
/** Two-letter avatar initials from a client name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts.length === 1 ? parts[0]!.slice(0, 2) : parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
}
export const AVATAR_TONES = ['coral', 'sun', 'sky', 'mint'] as const;
export const avatarTone = (i: number) => AVATAR_TONES[Math.abs(i) % AVATAR_TONES.length]!;
/** Deliverables as "done/total", or null when the client has none yet. */
export const deliverableRatio = (c: Pick<ClientView, 'deliverables'>): string | null => (c.deliverables && c.deliverables.total > 0 ? `${c.deliverables.done}/${c.deliverables.total}` : null);
/** Total leads across the loaded customers (unscored/onboarding customers count as zero). */
export const totalLeads = (clients: readonly Pick<ClientView, 'leads'>[]): number => clients.reduce((n, c) => n + (c.leads ?? 0), 0);

/** Plan / Owner / Service filters over the loaded client rows (the server already handles status, health, WhatsApp and search). */
export interface ClientFilters { plan?: string; owner?: string; service?: string }
export type ClientFilterKey = 'plan' | 'owner' | 'service';
export function clientFilterAvailability(rows: readonly ClientView[]): Record<ClientFilterKey, boolean> {
  return { plan: rows.some((r) => !!r.plan), owner: rows.some((r) => !!r.ownerId), service: rows.some((r) => (r.services?.length ?? 0) > 0) };
}
const sorted = (a: Iterable<string>) => [...new Set(a)].sort((x, y) => x.localeCompare(y));
export const clientPlanOptions = (rows: readonly ClientView[]): string[] => sorted(rows.flatMap((r) => (r.plan ? [r.plan] : [])));
export const clientServiceOptions = (rows: readonly ClientView[]): string[] => sorted(rows.flatMap((r) => r.services ?? []));
export function clientOwnerOptions(rows: readonly ClientView[]): { id: string; label: string }[] {
  const m = new Map<string, string>();
  for (const r of rows) if (r.ownerId && !m.has(r.ownerId)) m.set(r.ownerId, r.ownerLabel || r.ownerId);
  return [...m].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}
export const filterClients = (rows: readonly ClientView[], f: ClientFilters): ClientView[] =>
  rows.filter((r) => (!f.plan || r.plan === f.plan) && (!f.owner || r.ownerId === f.owner) && (!f.service || (r.services ?? []).includes(f.service)));

// ------------------------------------------------------------------ delivery calendar
export type CalStatus = 'draft' | 'internal' | 'customer' | 'scheduled' | 'published';
export const CAL_STATUSES: readonly CalStatus[] = ['draft', 'internal', 'customer', 'scheduled', 'published'];
/** Calendar status of a week item: its state first, then the review flags the calendar already carries. */
export function calStatus(item: Pick<WeekItem, 'state' | 'internalReview' | 'clientApproval'>): CalStatus {
  switch (item.state) {
    case 'published': return 'published';
    case 'approved': return 'scheduled';
    case 'client_review': return 'customer';
    case 'internal_review': return 'internal';
    default: break;
  }
  if (item.clientApproval === 'pending') return 'customer';
  if (item.internalReview) return 'internal';
  return 'draft';
}
/** "HH:MM" (UTC, the calendar's own time zone) of a due date, or '' when there is none. */
export function timeOf(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(11, 16);
}
export interface CalFilter { client?: string; status?: string; channel?: string; owner?: string }
export function filterWeekItems(items: readonly WeekItem[], f: CalFilter): WeekItem[] {
  return items.filter((i) => (!f.client || i.clientId === f.client) && (!f.status || calStatus(i) === f.status)
    && (!f.channel || i.type === f.channel) && (!f.owner || i.ownerId === f.owner));
}
/** Channel = the item's own type; options are only the types present in the loaded week. */
export const channelOptions = (items: readonly WeekItem[]): string[] => sorted(items.map((i) => i.type).filter(Boolean));
export function ownerOptions(items: readonly WeekItem[]): { id: string; label: string }[] {
  const m = new Map<string, string>();
  for (const i of items) if (i.ownerId && !m.has(i.ownerId)) m.set(i.ownerId, i.ownerLabel || i.ownerId);
  return [...m].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}
/** The next open item: the earliest not-yet-finished item due at or after now, else the earliest open one (already overdue). */
export function nextDue(items: readonly WeekItem[], now: Date): WeekItem | null {
  const open = items.filter((i) => !['published', 'cancelled', 'rejected'].includes(i.state) && i.dueAt).sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!));
  return open.find((i) => Date.parse(i.dueAt!) >= now.getTime()) ?? open[0] ?? null;
}
/** Items grouped by day in the order of `days` (the timeline view); days with nothing are left out. */
export function timelineGroups(items: readonly WeekItem[], days: readonly string[]): { day: string; items: WeekItem[] }[] {
  return days.map((day) => ({ day, items: items.filter((i) => i.day === day).sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? '') || a.title.localeCompare(b.title)) })).filter((g) => g.items.length > 0);
}
export type ChainStep = 'internal' | 'customer' | 'publish';
export interface ChainEntry { step: ChainStep; state: 'done' | 'current' | 'todo' }
/** The real approval path of the deliverable state machine: internal review, customer approval, publish. */
export function approvalChain(state: DeliverableState | string): ChainEntry[] {
  const at: Record<string, number> = { draft: 0, changes_requested: 0, rejected: 0, internal_review: 0, client_review: 1, approved: 2, published: 3, cancelled: 0 };
  const idx = at[state] ?? 0;
  const steps: ChainStep[] = ['internal', 'customer', 'publish'];
  return steps.map((step, i) => ({ step, state: i < idx ? 'done' : i === idx ? 'current' : 'todo' }));
}
/** The state a "Request approval" click moves a deliverable to (hand-driven, so the server state machine still decides). */
export function requestApprovalTarget(state: DeliverableState | string): DeliverableState | null {
  if (state === 'draft' || state === 'changes_requested') return 'internal_review';
  if (state === 'internal_review') return 'client_review';
  return null;
}

// ------------------------------------------------------------------ WhatsApp inbox (sample data only)
export type InboxTab = 'open' | 'ai' | 'snoozed' | 'closed';
export const INBOX_TABS: readonly InboxTab[] = ['open', 'ai', 'snoozed', 'closed'];
export interface SampleConversation {
  id: string; titleKey: string; previewKey: string; time: string; tab: InboxTab; aiDraft: boolean; aiHandling: boolean;
  lead: { id: string; phone: string; qualification: Record<'intent' | 'budget' | 'timeframe' | 'need' | 'fit', string> };
}
/**
 * Illustrative conversations. The partner API exposes no conversation endpoint (and customer conversations stay in the customer
 * workspace), so this is a preview of the inbox layout. It is always rendered under a "Sample data" badge.
 */
export const SAMPLE_CONVERSATIONS: readonly SampleConversation[] = [
  { id: 's1', titleKey: 'portal.partners.inbox.sample.c1.title', previewKey: 'portal.partners.inbox.sample.c1.preview', time: '09:42', tab: 'open', aiDraft: true, aiHandling: true,
    lead: { id: 'L-0001', phone: '+971500000001', qualification: { intent: 'high', budget: 'mid', timeframe: 'month', need: 'seo', fit: 'good' } } },
  { id: 's2', titleKey: 'portal.partners.inbox.sample.c2.title', previewKey: 'portal.partners.inbox.sample.c2.preview', time: '09:10', tab: 'ai', aiDraft: false, aiHandling: true,
    lead: { id: 'L-0002', phone: '+971500000002', qualification: { intent: 'mid', budget: 'low', timeframe: 'quarter', need: 'ads', fit: 'review' } } },
  { id: 's3', titleKey: 'portal.partners.inbox.sample.c3.title', previewKey: 'portal.partners.inbox.sample.c3.preview', time: '08:30', tab: 'snoozed', aiDraft: false, aiHandling: false,
    lead: { id: 'L-0003', phone: '+971500000003', qualification: { intent: 'low', budget: 'low', timeframe: 'later', need: 'content', fit: 'review' } } },
  { id: 's4', titleKey: 'portal.partners.inbox.sample.c4.title', previewKey: 'portal.partners.inbox.sample.c4.preview', time: 'Tue', tab: 'closed', aiDraft: false, aiHandling: false,
    lead: { id: 'L-0004', phone: '+971500000004', qualification: { intent: 'high', budget: 'high', timeframe: 'now', need: 'seo', fit: 'good' } } },
];
/** Conversations of a tab; "open" includes the ones the AI is handling (they are still open). */
export function filterConversations(list: readonly SampleConversation[], tab: InboxTab): SampleConversation[] {
  return list.filter((c) => (tab === 'open' ? c.tab === 'open' || c.tab === 'ai' : c.tab === tab));
}
export const inboxCounts = (list: readonly SampleConversation[]): Record<InboxTab, number> =>
  ({ open: filterConversations(list, 'open').length, ai: filterConversations(list, 'ai').length, snoozed: filterConversations(list, 'snoozed').length, closed: filterConversations(list, 'closed').length });

// ------------------------------------------------------------------ date labels
const dayFmt = (locale: string, opts: Intl.DateTimeFormatOptions, day: string) =>
  new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE-u-ca-gregory-nu-latn' : 'en-AE-u-ca-gregory', { ...opts, timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
/** "Thursday, 24 September" for the greeting. */
export const longDay = (locale: string, day: string) => dayFmt(locale, { weekday: 'long', day: 'numeric', month: 'long' }, day);
/** "Thu" for week strips and calendar heads. */
export const shortWeekday = (locale: string, day: string) => dayFmt(locale, { weekday: 'short' }, day);
/** Day of month as a number string. */
export const dayOfMonth = (locale: string, day: string) => dayFmt(locale, { day: 'numeric' }, day);
